import * as React from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  ArrowLeftRight,
  BarChart3,
  Boxes,
  FileSpreadsheet,
  FileText,
  PackageSearch,
  Printer,
  Smartphone,
  Tags,
  Truck,
  ChevronsLeft,
  ClipboardCheck,
  ClipboardList,
  LayoutDashboard,
  LogOut,
  Moon,
  ScrollText,
  Settings,
  ShieldAlert,
  Shuffle,
  Sun,
  Warehouse,
  Waves,
  ListChecks,
  Inbox,
} from 'lucide-react';
import { useSessao } from '@/hooks/useSessao';
import type { Permissao } from '@wms/shared';
import { useTema } from '@/hooks/useTema';
import { useNegativos } from '@/hooks/negativos';
import { useContexto } from '@/hooks/useContexto';
import { cn, numero } from '@/lib/utils';
import { IndicadorCarregando } from '@/componentes/layout/IndicadorCarregando';
import { BuscaUniversal } from '@/componentes/BuscaUniversal';
import { Botao } from '@/componentes/ui/botao';
import { SeletorContexto } from './SeletorContexto';
import { SeletorEmpresa } from './SeletorEmpresa';
import { SinoAlertas } from './SinoAlertas';
import { AvisoImpressao } from './AvisoImpressao';
import { BotaoAssistente, ChatbotManifest } from './ChatbotManifest';

interface Destino {
  para: string;
  rotulo: string;
  descricao: string;
  icone: React.ReactNode;
  /** Prefixo que também marca o item como ativo (rotas irmãs). */
  prefixo?: string;
  /** Chave do contador exibido como selo no item. */
  selo?: 'negativos';
  /** Só aparece para quem tem esta permissão (admin master vê tudo). */
  permissao?: Permissao;
}

// Quatro destinos. Menu longo é sintoma de modelo mental confuso (blueprint).
// Movimentos abre direto em Negativos — a aba que funciona e tem valor.
const DESTINOS: Destino[] = [
  {
    para: '/',
    rotulo: 'Início',
    descricao: 'Visão geral do CD',
    icone: <LayoutDashboard className="h-[18px] w-[18px]" />,
  },
  {
    para: '/estoque',
    rotulo: 'Estoque',
    descricao: 'Mapa e posições',
    icone: <Boxes className="h-[18px] w-[18px]" />,
  },
  {
    para: '/movimentos/negativos',
    rotulo: 'Movimentos',
    descricao: 'Fila de trabalho',
    icone: <ArrowLeftRight className="h-[18px] w-[18px]" />,
    prefixo: '/movimentos',
    selo: 'negativos',
  },
  {
    para: '/transferencias',
    rotulo: 'Transferências',
    descricao: 'Mover entre posições',
    icone: <Shuffle className="h-[18px] w-[18px]" />,
  },
  {
    // Um item só: escopos, congelar, contagens, fechamentos, integração e
    // consultas moram dentro de cada inventário, não em submenus.
    para: '/inventario',
    rotulo: 'Inventário',
    descricao: 'Escopos, contagens e andamento',
    icone: <ClipboardList className="h-[18px] w-[18px]" />,
    prefixo: '/inventario',
    // No lançamento, só o papel "Inventário" (e o admin master).
    permissao: 'inventario.ver',
  },
  {
    para: '/kardex',
    rotulo: 'Kardex',
    descricao: 'Auditoria de movimentos',
    icone: <ScrollText className="h-[18px] w-[18px]" />,
  },
  {
    para: '/analises',
    rotulo: 'Análises',
    descricao: 'Perguntas rápidas',
    icone: <BarChart3 className="h-[18px] w-[18px]" />,
  },
  {
    para: '/relatorios',
    rotulo: 'Relatórios',
    descricao: 'Consulta e exportação',
    icone: <FileSpreadsheet className="h-[18px] w-[18px]" />,
  },
];

/**
 * Operação é o trabalho que acontece no chão — hoje, a separação. Fica em
 * seção própria porque é outro modo de uso: o de Estoque é consulta e
 * correção; o de Operação é a fila de trabalho do dia.
 */
const DESTINOS_OPERACAO: Destino[] = [
  {
    para: '/entradas',
    rotulo: 'Entradas',
    descricao: 'NF de compra: da OC ao estoque',
    icone: <Inbox className="h-[18px] w-[18px]" />,
    permissao: 'entradas.ver',
  },
  {
    para: '/recebimento',
    rotulo: 'Recebimento',
    descricao: 'Conferência cega em A4',
    icone: <PackageSearch className="h-[18px] w-[18px]" />,
  },
  {
    para: '/separacao',
    rotulo: 'Separação',
    descricao: 'Ondas de picking',
    icone: <Waves className="h-[18px] w-[18px]" />,
  },
  {
    para: '/conferencia',
    rotulo: 'Conferência',
    descricao: 'O que foi separado, conferido',
    icone: <ClipboardCheck className="h-[18px] w-[18px]" />,
  },
  {
    para: '/faturamento',
    rotulo: 'Faturamento',
    descricao: 'Conferido vira nota',
    icone: <FileText className="h-[18px] w-[18px]" />,
  },
  {
    para: '/romaneios',
    rotulo: 'Romaneios',
    descricao: 'Achar, conferir e reimprimir',
    icone: <Truck className="h-[18px] w-[18px]" />,
  },
  {
    para: '/etiquetas',
    rotulo: 'Etiquetas',
    descricao: 'Marketplace: por que a etiqueta não saiu',
    icone: <Tags className="h-[18px] w-[18px]" />,
  },
  {
    para: '/etiquetas-lista',
    rotulo: 'Etiquetas da lista',
    descricao: 'Separação, DANFE e ZPL por lista',
    icone: <ListChecks className="h-[18px] w-[18px]" />,
  },
  {
    para: '/expedicao',
    rotulo: 'Expedição ML',
    descricao: 'O que o Mercado Livre espera, e de quem cobrar',
    icone: <Truck className="h-[18px] w-[18px]" />,
  },
];

const DESTINO_ADMIN: Destino = {
  para: '/admin',
  rotulo: 'Admin',
  descricao: 'Acessos e permissões',
  icone: <Settings className="h-[18px] w-[18px]" />,
};

const CHAVE_RECOLHIDA = 'wms.rail.recolhida';

export function AppShell(): JSX.Element {
  const { usuario } = useSessao();
  const { centro, acessoNegado } = useContexto();
  const negativos = useNegativos(centro);
  const ehAdmin = usuario?.adminMaster ?? false;

  const [recolhida, definirRecolhida] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(CHAVE_RECOLHIDA) === '1';
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem(CHAVE_RECOLHIDA, recolhida ? '1' : '0');
    } catch {
      /* storage bloqueado */
    }
  }, [recolhida]);

  const selos = { negativos: negativos.data?.total ?? 0 };

  return (
    <div className="flex h-full bg-fundo">
      <BarraLateral
        admin={ehAdmin}
        recolhida={recolhida}
        aoAlternarRecolhida={() => definirRecolhida((v) => !v)}
        selos={selos}
      />

      {/*
        A FOLGA DA FITA.

        A fita do menu avança 22px para dentro desta coluna, e o conteúdo das
        páginas começa aos 20px do próprio padding — ou seja, encostavam. Os
        12px daqui empurram tudo para 32px e deixam ~10px de respiro entre a
        fita e o primeiro cartão.

        O padding está na COLUNA e não só no `main` de propósito: aplicado
        apenas ao conteúdo, o cabeçalho continuaria alinhado à borda e as duas
        réguas verticais da tela deixariam de coincidir. E é `sm:` porque no
        celular não há barra lateral nem fita — lá o padding só comeria tela.
      */}
      <div className="flex min-w-0 flex-1 flex-col sm:pl-3">
        <Cabecalho />
        <main className="min-h-0 flex-1 overflow-y-auto pb-[72px] sm:pb-0 sm:pl-[22px]">
          {acessoNegado ? <AcessoNegado pedido={acessoNegado} /> : <Outlet />}
        </main>

        {/* Um só para a aplicação inteira — inclusive a troca de página, que
            os overlays por painel não cobriam. */}
        <IndicadorCarregando />
        <ChatbotManifest />
      </div>

      <BarraInferior admin={ehAdmin} selos={selos} />
    </div>
  );
}

// --- Barra lateral ---------------------------------------------------------

function BarraLateral({
  admin,
  recolhida,
  aoAlternarRecolhida,
  selos,
}: {
  admin: boolean;
  recolhida: boolean;
  aoAlternarRecolhida: () => void;
  selos: { negativos: number };
}): JSX.Element {
  const { centroAtual } = useContexto();
  const { temPermissao } = useSessao();

  return (
    <aside
      className={cn(
        'gradiente-rail relative hidden shrink-0 flex-col text-rail-texto transition-[width] duration-300 ease-suave sm:flex',
        recolhida ? 'w-[76px]' : 'w-[236px]',
      )}
    >
      {/* Brilho de marca no topo da barra — profundidade sem custo de leitura. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-56 opacity-60"
        style={{
          background:
            'radial-gradient(120% 80% at 20% 0%, rgb(var(--cor-primaria) / 0.45), transparent 70%)',
        }}
      />

      {/*
        A MARCA LEVA PARA A VISÃO GERAL.

        É a convenção que todo mundo já traz de fora — clicar no logo volta ao
        começo — e aqui ela não custa espaço nem item novo: o bloco já estava
        ali, só não fazia nada.

        RESOLVE UM PROBLEMA CONCRETO: o item "Início" é o primeiro do `nav`, e
        o `nav` ROLA. Em tela baixa, ou com o menu de admin aberto, ele sai de
        vista e o caminho de volta simplesmente some. A marca fica FORA da área
        que rola — nunca sai. Recolhida, o alvo vira o quadrado da marca, no
        mesmo lugar.
      */}
      <NavLink
        to="/"
        end
        title="Visão geral do CD"
        className={cn(
          'relative flex h-[60px] items-center gap-2.5 px-4 transition-colors hover:bg-white/[0.06]',
          recolhida && 'justify-center px-0',
        )}
      >
        <span className="gradiente-marca grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[15px] font-black text-white shadow-marca">
          W
        </span>
        {!recolhida ? (
          <span className="min-w-0 leading-tight">
            <span className="block text-[15px] font-bold tracking-[-0.02em] text-white">WMS</span>
            <span className="block text-[10.5px] font-semibold uppercase tracking-[0.12em] text-rail-textoFraco">
              Megamamute
            </span>
          </span>
        ) : null}
      </NavLink>

      <nav
        aria-label="Navegação principal"
        className="relative flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 py-3"
      >
        {!recolhida ? (
          <p className="mb-1 px-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-rail-textoFraco/70">
            Estoque
          </p>
        ) : null}
        {DESTINOS.filter((d) => !d.permissao || temPermissao(d.permissao)).map((destino) => (
          <ItemRail key={destino.para} destino={destino} recolhida={recolhida} selos={selos} />
        ))}

        <div className="my-2 h-px bg-rail-borda" />
        {!recolhida ? (
          <p className="mb-1 px-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-rail-textoFraco/70">
            Operação
          </p>
        ) : null}
        {DESTINOS_OPERACAO.filter((d) => !d.permissao || temPermissao(d.permissao)).map((destino) => (
          <ItemRail key={destino.para} destino={destino} recolhida={recolhida} selos={selos} />
        ))}

        {admin ? (
          <>
            <div className="my-2 h-px bg-rail-borda" />
            {!recolhida ? (
              <p className="mb-1 px-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-rail-textoFraco/70">
                Configuração
              </p>
            ) : null}
            <ItemRail destino={DESTINO_ADMIN} recolhida={recolhida} selos={selos} />
          </>
        ) : null}
      </nav>

      {/* Rodapé: o centro ativo, visível o tempo todo porque toda tela é lida
          "dentro" dele. O recolher saiu daqui — virou a pastilha da borda. */}
      <div
        className={cn(
          'relative p-3',
          !recolhida && centroAtual ? 'border-t border-rail-borda' : 'hidden',
        )}
      >
        {!recolhida && centroAtual ? (
          <div className="mb-2 rounded-xl bg-white/[0.06] px-3 py-2.5 ring-1 ring-inset ring-white/10">
            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-rail-textoFraco">
              <Warehouse className="h-3 w-3" />
              Centro ativo
            </p>
            <p className="mt-1 truncate text-[13px] font-semibold text-white">
              {centroAtual.centroEstoque} · {centroAtual.nome}
            </p>
          </div>
        ) : null}
      </div>

      {/*
        O ALTERNADOR SAIU DO RODAPÉ E VIROU PASTILHA NA BORDA.

        No pé da barra ele estava no lugar mais longe de onde a mão já está: a
        pessoa navega no meio da lista, e recolher exigia descer os olhos e o
        cursor até o fim. Na borda direita, na altura do meio, ele fica no
        caminho natural entre o menu e o conteúdo — que é exatamente a fronteira
        que o botão move.

        É uma FITA, e não um botão redondo. A bolinha flutuava solta sobre o
        conteúdo e parecia um controle de outra coisa; a fita nasce colada na
        borda e continua a barra para fora — lê-se como a aba da divisória, que
        é exatamente o que ela move. Por isso só os cantos de FORA são
        arredondados: o lado que encosta no menu tem de parecer o mesmo corpo.
      */}
      <button
        type="button"
        onClick={aoAlternarRecolhida}
        aria-label={recolhida ? 'Expandir menu' : 'Recolher menu'}
        title={recolhida ? 'Expandir menu' : 'Recolher menu'}
        className={cn(
          'group absolute -right-[22px] top-1/2 z-20 hidden h-[72px] w-[22px]',
          '-translate-y-1/2 place-items-center rounded-r-xl',
          'gradiente-rail text-rail-textoFraco shadow-media ring-1 ring-inset ring-rail-borda',
          'transition-colors hover:text-white',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primaria/60 sm:grid',
        )}
      >
        {/* A marca acende no hover: a fita é discreta em repouso, porque fica
            permanentemente no campo de visão de quem está lendo o conteúdo. */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-r-xl bg-primaria opacity-0 transition-opacity group-hover:opacity-100"
        />
        <ChevronsLeft
          className={cn(
            'relative h-3.5 w-3.5 transition-transform duration-300',
            recolhida && 'rotate-180',
          )}
        />
      </button>
    </aside>
  );
}

function ItemRail({
  destino,
  recolhida,
  selos,
}: {
  destino: Destino;
  recolhida: boolean;
  selos: { negativos: number };
}): JSX.Element {
  const local = useLocation();
  const ativoPorPrefixo = destino.prefixo ? local.pathname.startsWith(destino.prefixo) : false;
  const contagem = destino.selo ? selos[destino.selo] : 0;

  return (
    <NavLink
      to={destino.para}
      end={destino.para === '/'}
      title={recolhida ? destino.rotulo : undefined}
      className={({ isActive }) => {
        const ativo = isActive || ativoPorPrefixo;
        return cn(
          'group relative flex min-h-[46px] items-center gap-3 rounded-xl px-3 transition-all duration-200',
          recolhida && 'justify-center px-0',
          ativo
            ? 'bg-white/[0.13] text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.1)]'
            : 'text-rail-textoFraco hover:bg-white/[0.07] hover:text-white',
        );
      }}
    >
      {({ isActive }) => {
        const ativo = isActive || ativoPorPrefixo;
        return (
          <>
            {/* Barra de acento à esquerda: o "você está aqui" em 2px. */}
            <span
              aria-hidden
              className={cn(
                'absolute left-0 top-1/2 w-[3px] -translate-y-1/2 rounded-r-full bg-gradient-to-b from-primaria to-primaria-dois transition-all duration-300',
                ativo ? 'h-6 opacity-100' : 'h-0 opacity-0',
              )}
            />
            <span className={cn('shrink-0 transition-transform', ativo && 'scale-105')}>
              {destino.icone}
            </span>
            {!recolhida ? (
              <span className="min-w-0 flex-1 leading-tight">
                <span className="block truncate text-[13.5px] font-semibold">{destino.rotulo}</span>
                <span
                  className={cn(
                    'block truncate text-[10.5px] transition-colors',
                    ativo ? 'text-white/55' : 'text-rail-textoFraco/70',
                  )}
                >
                  {destino.descricao}
                </span>
              </span>
            ) : null}
            {contagem > 0 ? (
              <span
                className={cn(
                  'tabular shrink-0 rounded-full bg-status-divergencia px-1.5 py-0.5 text-[10px] font-bold text-white',
                  recolhida && 'absolute right-2 top-2 px-1 py-0 text-[9px]',
                )}
              >
                {contagem > 99 ? '99+' : numero(contagem)}
              </span>
            ) : null}
          </>
        );
      }}
    </NavLink>
  );
}

// --- Barra inferior (coletor / celular) ------------------------------------

function BarraInferior({
  admin,
  selos,
}: {
  admin: boolean;
  selos: { negativos: number };
}): JSX.Element {
  const local = useLocation();
  const { temPermissao } = useSessao();
  const permitidos = DESTINOS.filter((d) => !d.permissao || temPermissao(d.permissao));
  const destinos = admin ? [...permitidos, DESTINO_ADMIN] : permitidos;

  return (
    <nav
      aria-label="Navegação principal"
      className="vidro fixed inset-x-0 bottom-0 z-30 flex border-t border-borda pb-[env(safe-area-inset-bottom)] sm:hidden"
    >
      {destinos.map((destino) => {
        const ativoPorPrefixo = destino.prefixo
          ? local.pathname.startsWith(destino.prefixo)
          : false;
        const contagem = destino.selo ? selos[destino.selo] : 0;
        return (
          <NavLink
            key={destino.para}
            to={destino.para}
            end={destino.para === '/'}
            className={({ isActive }) =>
              cn(
                'relative flex min-h-[60px] flex-1 flex-col items-center justify-center gap-1 text-[10.5px] font-semibold transition-colors',
                isActive || ativoPorPrefixo ? 'text-primaria' : 'text-textoFraco',
              )
            }
          >
            {({ isActive }) => (
              <>
                <span
                  aria-hidden
                  className={cn(
                    'absolute inset-x-4 top-0 h-[3px] rounded-b-full gradiente-marca transition-opacity',
                    isActive || ativoPorPrefixo ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {destino.icone}
                {destino.rotulo}
                {contagem > 0 ? (
                  <span className="absolute right-[22%] top-2 h-2 w-2 rounded-full bg-status-divergencia ring-2 ring-superficie" />
                ) : null}
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

// --- Cabeçalho -------------------------------------------------------------

function Cabecalho(): JSX.Element {
  const { usuario, sair } = useSessao();
  const { escuro, alternar } = useTema();
  const navegar = useNavigate();

  const iniciais = (usuario?.nome ?? '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((parte) => parte.charAt(0).toUpperCase())
    .join('');

  return (
    <header className="vidro sticky top-0 z-20 flex h-[60px] shrink-0 items-center gap-2 border-b border-borda px-3 sm:px-4">
      {/* Marca só no celular: no desktop ela vive na barra lateral. */}
      <span className="gradiente-marca grid h-8 w-8 shrink-0 place-items-center rounded-lg text-sm font-black text-white sm:hidden">
        W
      </span>

      {/* Empresa antes do centro: ela decide QUAIS centros existem na lista. */}
      <SeletorEmpresa />
      <SeletorContexto />

      <div className="ml-auto flex items-center gap-1.5">
        <BuscaUniversal />

        <AvisoImpressao />

        <BotaoAssistente />

        <SinoAlertas />

        <button
          type="button"
          onClick={alternar}
          aria-label={escuro ? 'Usar tema claro' : 'Usar tema escuro'}
          className="hidden h-10 w-10 items-center justify-center rounded-lg text-textoFraco transition-colors hover:bg-superficie2 hover:text-texto sm:flex"
        >
          {escuro ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </button>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="group flex items-center gap-2 rounded-xl p-1 pr-1.5 transition-colors hover:bg-superficie2"
              aria-label={`Conta de ${usuario?.nome ?? 'usuário'}`}
            >
              <span className="gradiente-marca grid h-9 w-9 place-items-center rounded-lg text-2xs font-bold text-white shadow-xs">
                {iniciais}
              </span>
              <span className="hidden min-w-0 max-w-[130px] text-left leading-tight lg:block">
                <span className="block truncate text-[12.5px] font-semibold">{usuario?.nome}</span>
                <span className="block truncate text-[10.5px] text-textoFraco">
                  {usuario?.adminMaster ? 'admin master' : usuario?.login}
                </span>
              </span>
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              className="z-50 w-60 overflow-hidden rounded-xl border border-borda bg-superficie p-1.5 shadow-forte data-[state=open]:animate-escalar"
            >
              <div className="flex items-center gap-2.5 px-2 py-2">
                <span className="gradiente-marca grid h-9 w-9 shrink-0 place-items-center rounded-lg text-2xs font-bold text-white">
                  {iniciais}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-semibold">{usuario?.nome}</p>
                  <p className="truncate text-[11px] text-textoFraco">
                    {usuario?.login}
                    {usuario?.adminMaster ? ' · admin master' : ''}
                  </p>
                </div>
              </div>
              <DropdownMenu.Separator className="my-1 h-px bg-borda" />
              <DropdownMenu.Item
                className="flex min-h-[40px] cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-medium data-[highlighted]:bg-superficie2 data-[highlighted]:outline-none sm:hidden"
                onSelect={(e) => {
                  e.preventDefault();
                  alternar();
                }}
              >
                {escuro ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {escuro ? 'Tema claro' : 'Tema escuro'}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex min-h-[40px] cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-medium data-[highlighted]:bg-superficie2 data-[highlighted]:outline-none"
                onSelect={() => navegar('/impressao-etiquetas')}
              >
                <Printer className="h-4 w-4" />
                Impressão de etiquetas
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex min-h-[40px] cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-medium data-[highlighted]:bg-superficie2 data-[highlighted]:outline-none"
                onSelect={() => navegar('/versoes-coletor')}
              >
                <Smartphone className="h-4 w-4" />
                Versões App WMS Coletores
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex min-h-[40px] cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-medium text-status-divergencia data-[highlighted]:bg-status-divergenciaFundo data-[highlighted]:outline-none"
                onSelect={() => {
                  void sair().then(() => navegar('/entrar'));
                }}
              >
                <LogOut className="h-4 w-4" />
                Sair
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}

/**
 * A URL pediu um centro ou uma empresa que este usuário não alcança.
 *
 * POR QUE PARAR A TELA, e não só corrigir o número
 * ------------------------------------------------
 * O servidor nunca entregou esse dado: toda rota que lê estoque passa por
 * `exigirCentro`/`exigirEmpresa`, e `/api/contexto` só devolve o que é do
 * usuário. Trocar o número na barra jamais leu galpão alheio.
 *
 * O problema era o SILÊNCIO. A tela descartava o centro inválido, caía no
 * primeiro permitido e reescrevia a URL — quem abrisse `?centro=3201` sem
 * acesso via os números do 2201 com a barra dizendo outra coisa por um
 * instante. E o caso comum disso não é alguém tentando burlar: é um link
 * colado de um colega que tem outro acesso. Conferir o galpão errado sem ser
 * avisado é pior do que ouvir "não".
 *
 * Por isso a mensagem diz exatamente O QUE foi pedido — o número tem de
 * aparecer para a pessoa saber o que mandar liberar —, sem dizer se aquele
 * centro existe. Quem não tem acesso não descobre por aqui a diferença entre
 * "não é seu" e "não existe"; é a mesma resposta que o servidor dá.
 */
function AcessoNegado({
  pedido,
}: {
  pedido: { centro: number | null; empresa: number | null };
}): JSX.Element {
  const { centros, definirCentro, centroAtual } = useContexto();
  const alternativa = centroAtual ?? centros[0] ?? null;

  const oQue =
    pedido.centro !== null && pedido.empresa !== null
      ? `o centro ${pedido.centro} da empresa ${pedido.empresa}`
      : pedido.centro !== null
        ? `o centro de estoque ${pedido.centro}`
        : `a empresa ${pedido.empresa}`;

  return (
    <div className="mx-auto grid max-w-lg place-items-center px-4 py-16 text-center">
      <span className="mb-3 grid h-12 w-12 place-items-center rounded-full bg-status-divergenciaFundo text-status-divergencia">
        <ShieldAlert className="h-6 w-6" />
      </span>
      <h1 className="text-[17px] font-bold">Sem acesso a {oQue}</h1>
      <p className="mt-2 text-[13px] leading-relaxed text-textoFraco">
        O endereço pede um lugar que não está liberado para o seu usuário. Nada foi carregado —
        nenhuma tela do sistema mostra dado de centro que você não pode abrir.
      </p>
      <p className="mt-1.5 text-[12.5px] leading-relaxed text-textoFraco">
        Se você precisa mesmo trabalhar nele, peça a liberação ao administrador. Se o link veio de
        um colega, é provável que ele tenha um acesso que você ainda não tem.
      </p>

      {alternativa ? (
        <Botao
          variante="primaria"
          tamanho="pequeno"
          className="mt-5"
          onClick={() => definirCentro(alternativa.centroEstoque)}
        >
          Ir para {alternativa.centroEstoque} · {alternativa.nome}
        </Botao>
      ) : null}
    </div>
  );
}
